import React from "react";
import PageLayout from "lib/components/PageLayout";
import { GetStaticProps } from "next";
import dayjs from "dayjs";
import renderToString from "next-mdx-remote/render-to-string";
import hydrate from "next-mdx-remote/hydrate";
import { MdxRemote } from "next-mdx-remote/types";
import slug from "rehype-slug";
import urls from "rehype-urls";
import Wrapper from "lib/components/Wrapper";
import stylesMDX from "domain/resources/stylesMDX";
import componentsMDX from "domain/resources/componentsMDX";
import Head from "lib/components/Head";
import CommonService from "domain/common/commonService";

interface TermsOfServiceProps {
  mdxSource: MdxRemote.Source;
  title: string;
  lastUpdateDate?: string;
}

const TermsOfService: React.FC<TermsOfServiceProps> = ({
  mdxSource,
  title,
  lastUpdateDate,
}) => {
  // hydrate converted mdx content with custom components
  const content = hydrate(mdxSource, { ...componentsMDX });

  return (
    <PageLayout>
      <Head title="Terms of Service | Lawfully" url="/terms-of-service" />
      <Wrapper>
        <h1
          css={`
            margin-bottom: 30px;
            line-height: 42px;
            font-size: 30px;
          `}
        >
          {title}
        </h1>
        {lastUpdateDate && (
          <div
            css={`
              margin-bottom: 30px;
              line-height: 24px;
              font-size: 15px;
              font-weight: 500;
            `}
          >
            Last updated: {dayjs(lastUpdateDate).format("MMMM DD, YYYY")}
          </div>
        )}
        <div
          css={[
            stylesMDX,
            `
           ol {
            list-style-type: lower-alpha;
           }
           ol > li > ol {
            list-style-type: lower-roman;
           }
           padding-bottom: 30px;
           `,
          ]}
        >
          {content}
        </div>
      </Wrapper>
    </PageLayout>
  );
};

export default TermsOfService;

export const getStaticProps: GetStaticProps = async ({ preview }) => {
  const commonService = new CommonService();
  const data = await commonService.getPolicy({
    slug: "terms-of-service",
    preview: !!preview,
  });

  if (!data) {
    return { notFound: true };
  }

  const { content, title, lastUpdateDate } = data;
  // render mdx content into html string
  const mdxSource = await renderToString(content, {
    ...componentsMDX,
    mdxOptions: {
      rehypePlugins: [
        slug,
        [
          urls,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore Settings type doesn't allow function types for now
          (url, node) => {
            if (url.host || url.path) {
              // non-null host means that it's not an internal fragment link (used by toc)
              // non-null path means that it's an internal link (to other pages)
              node.properties.target = "_blank";
              node.properties.rel = "noopener noreferrer";
            }
          },
        ],
      ],
    },
  });

  return {
    props: {
      mdxSource,
      title,
      lastUpdateDate,
    },
    revalidate: 12 * 24 * 60, // 12 hours
  };
};
